// What is inside a number on the activity board.
//
// Opened by any of the four funnel stages and by seven of the tiles above them. Deliberately a
// list rather than the book's company panel: the question somebody has when they click a number
// is "which accounts are these", not "tell me everything about one of them" -- and the answer to
// the second is one click further on, in Salesforce.
//
// **Most rows link to the account in Salesforce, not to the execution board.** In conversation
// is the exception: the row expands into the thread with that strategist, and Salesforce is
// a link beside it. The other drawers stay a checking list — the record lives in the CRM.
//
// The label and the sentence under it are the payload's, not this component's. `activity.py`
// writes them next to the code that builds each list, which is what stops a drawer describing a
// list it does not contain.

import { useEffect, useRef, useState } from 'react';
import type { AttendanceVerification, StageMember } from '../api/types';
import { AttendanceEvidence } from './AttendanceEvidence';
import { GongLinks } from './GongLinks';
import { SALESFORCE_ACCOUNT_URL, formatCount, formatMoney } from '../lib/format';
import { AccountThread } from './AccountThread';

// What the caller is asking to see. A key, its wording, and the number the tile printed -- which
// is what lets the drawer say "showing the 200 most recent of 431" without recounting anything.
export interface DrawerRequest {
  key: string;
  label: string;
  definition: string;
  total: number;
}

export function StageDrawer({
  request,
  members,
  attendanceEvidence,
  selfBooked,
  onClose,
}: {
  request: DrawerRequest | null;
  members: StageMember[];
  attendanceEvidence?: AttendanceVerification;
  selfBooked?: Record<string, StageMember[]>;
  onClose: () => void;
}) {
  const isOpen = Boolean(request);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [openAccountId, setOpenAccountId] = useState<string | null>(null);
  const opensThread = request?.key === 'replied';
  const excluded = request ? selfBooked?.[request.key] ?? [] : [];

  // Escape closes it, and focus moves in when it opens. Same contract the sidebar has, so the
  // two behave identically from the keyboard even though they render different things.
  useEffect(() => {
    if (!isOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) closeRef.current?.focus();
  }, [isOpen, request?.key]);

  useEffect(() => {
    setOpenAccountId(null);
  }, [request?.key]);

  useEffect(() => {
    const background = document.getElementById('app-background');
    if (!background) return;
    if (isOpen) background.setAttribute('inert', '');
    else background.removeAttribute('inert');
    return () => background.removeAttribute('inert');
  }, [isOpen]);

  return (
    <>
      <div className={'scrim' + (isOpen ? ' on' : '')} onClick={onClose} />
      <aside
        className={'side drawer' + (isOpen ? ' on' : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawername"
        aria-hidden={!isOpen}
      >
        {request ? (
          <>
            <div className="sidehead">
              <button className="sideclose" ref={closeRef} aria-label="Close" onClick={onClose}>
                ×
              </button>
              <h2 id="drawername">{request.label}</h2>
              <p className="sidesub">{request.definition}</p>
              {opensThread ? (
                <p className="sidesub">
                  Threads are the Salesforce mirror — Fivetran syncs around 02:00 and 14:00
                  Pacific, so this morning's reply may not be here yet.
                </p>
              ) : null}
            </div>

            <div className="sidebody">
              {excluded.length > 0 ? <details className="attendance-evidence">
                <summary>Self-booked · {excluded.length} excluded from team SDR / Scout credit</summary>
                {excluded.map((member, index) => <p key={`${member.account_id}-${index}`}>
                  {member.account} · {member.day} · {member.who ?? 'Meeting'} · Self-booked
                </p>)}
              </details> : null}
              {attendanceEvidence && (request.key === 'met' || request.key === 'attended')
                ? <AttendanceEvidence evidence={attendanceEvidence} /> : null}
              {members.length ? (
                <>
                  {/* Say when the list is shorter than the number above it. A drawer that
                      silently showed the first 200 of 400 would make the board look wrong. */}
                  {members.length < request.total ? (
                    <p className="covnote">
                      Showing the {formatCount(members.length)} most recent of{' '}
                      {formatCount(request.total)}.
                    </p>
                  ) : null}
                  <div className="memberlist">
                    {members.map((member, index) => {
                      const threadOpen = opensThread && openAccountId === member.account_id;
                      return (
                        <div
                          className={'memberblock' + (threadOpen ? ' on' : '')}
                          key={`${member.account_id}-${index}`}
                        >
                          <div className="memberrow">
                            {opensThread ? (
                              <>
                                <button
                                  type="button"
                                  className="mopen"
                                  aria-expanded={threadOpen}
                                  onClick={() => setOpenAccountId(current => (
                                    current === member.account_id ? null : member.account_id
                                  ))}
                                  title="Show the thread with this strategist"
                                >
                                  <span className="mday">{member.day ?? '—'}</span>
                                  <span className="mname">
                                    {member.account}
                                    {member.who ? <span className="sm">{member.who}</span> : null}
                                  </span>
                                  <span className="mmeta">
                                    <span>
                                      {member.sdr_owner}
                                      {member.detail ? ` · ${member.detail}` : ''}
                                    </span>
                                    {member.extra ? <span className="sm">{member.extra}</span> : null}
                                  </span>
                                </button>
                                <a
                                  className="msf"
                                  href={`${SALESFORCE_ACCOUNT_URL}${member.account_id}/view`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open this account in Salesforce"
                                >
                                  Salesforce
                                </a>
                              </>
                            ) : (
                              <>
                                <span className="mday">{member.day ?? '—'}</span>
                                <span className="mname">
                                  {/* Opens in a new tab: the drawer is a checking tool, and
                                      somebody verifying five rows should not lose the board
                                      on the first one. */}
                                  <a
                                    href={`${SALESFORCE_ACCOUNT_URL}${member.account_id}/view`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open this account in Salesforce"
                                  >
                                    {member.account}
                                  </a>
                                  {member.who ? <span className="sm">{member.who}</span> : null}
                                </span>
                                <span className="mmeta">
                                  {member.amount != null ? (
                                    <b className="mamount">{formatMoney(member.amount)}</b>
                                  ) : null}
                                  <span>
                                    {member.sdr_owner}
                                    {member.detail ? ` · ${member.detail}` : ''}
                                  </span>
                                  {member.extra ? <span className="sm">{member.extra}</span> : null}
                                  <GongLinks urls={member.gong_call_urls} />
                                </span>
                              </>
                            )}
                          </div>
                          {threadOpen ? (
                            <AccountThread
                              accountId={member.account_id}
                              accountName={member.account}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="covnote">
                  Nothing here in this period. That is the finding rather than a gap — widen the
                  window, or check the number above this one.
                </p>
              )}
            </div>
          </>
        ) : null}
      </aside>
    </>
  );
}
